'use client';

import Link from "next/link";

interface Gallery {
  id: number;
  slug: string;
  title: string;
  description?: string | null;
  eventStartDate: string;
  eventEndDate: string | null;
  coverImage?: {
    url: string;
  } | null; 
  pictures: { 
    id: number; 
    url: string;
  }[];
}

interface GalleryListProps {
  galleries: Gallery[];
}

const GalleryList = ({ galleries }: GalleryListProps) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-GB", {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  // Safe description handler
  const getDescription = (description?: string | null) => {
    return description || "";
  };

  // Safe image URL handler
  const getImageUrl = (coverImage?: { url: string } | null) => {
    return coverImage?.url || "/assets/images/placeholder.png";
  };

  // Safe date range formatter
  const getDateRange = (startDate: string, endDate: string | null) => {
    if (!endDate || startDate === endDate) {
      return formatDate(startDate);
    }
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  return (
    <div className="p-4 rounded-md mb-2">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-4">
        {galleries.length === 0 ? (
          <p className="text-gray-500 text-sm col-span-full text-center">
            No galleries available
          </p>
        ) : (
          galleries.map((gallery) => (
            <div
              key={gallery.slug}
              className="group relative bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition"
            >
              <Link href={`/galleries/${gallery.slug}`} className="block h-full">
                {/* Cover Image */}
                <div className="aspect-square relative">
                  <img
                    src={getImageUrl(gallery.coverImage)}
                    alt={gallery.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback if image fails to load
                      e.currentTarget.src = "/placeholder-image.jpg";
                    }}
                  />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors" />
                </div>

                {/* Gallery Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white bg-gradient-to-t from-black/60 to-transparent">
                  <h3 className="text-[14px] md:text-lg font-bold truncate">{gallery.title}</h3>
                  <div className="flex justify-between items-center mt-2">
                    <span className="hidden md:block text-sm">
                      {gallery.pictures.length} photo{gallery.pictures.length !== 1 ? 's' : ''}
                    </span>
                    <span className="text-xs">
                      {getDateRange(gallery.eventStartDate, gallery.eventEndDate)}
                    </span>
                  </div>
                </div>
              </Link>

              {/* Hover Expand Preview */}
              <div className="hidden group-hover:block absolute top-full left-0 right-0 z-10 bg-white shadow-lg mt-2 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-2 truncate">
                  {getDescription(gallery.description)}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {gallery.pictures.slice(0, 6).map((picture) => (
                    <img
                      key={picture.id}
                      src={picture.url}
                      alt=""
                      className="aspect-square object-cover rounded"
                      onError={(e) => {
                        e.currentTarget.src = "/placeholder-image.jpg";
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default GalleryList;